/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Proposal` for the Caintra Connect frontend.
 * @module modals/ProposalsListModal/Proposal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./ProposalsListModal.module.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { IconButton } from "@mui/material";
import AssetImage from "../../components/AssetImage";

const Proposal = ({
  proposal,
  user,
  company,
  onProposalAction,
  onViewProposal,
  sectionType,
}) => {
  const { t } = useTranslation();
  const isAccepted = sectionType === "accepted";
  const isRejected = sectionType === "rejected";

  const handleProposalClick = (e) => {
    // Check if the click was on a button or its children
    if (e.target.closest(".MuiIconButton-root")) {
      return;
    }
    onViewProposal(proposal);
  };

  return (
    <div
      className={styles.proposalContainer}
      onClick={handleProposalClick}
      style={{ cursor: "pointer" }}
    >
      <div className={styles.proposal}>
        <div className={styles.userInfoContainer}>
          {proposal.esDestacado ? (
            <StarIcon
              className={styles.starIcon}
              onClick={(e) => {
                e.stopPropagation();
                onProposalAction(proposal._id, "unstar");
              }}
            />
          ) : (
            <StarOutlineIcon
              className={styles.starIcon}
              onClick={(e) => {
                e.stopPropagation();
                onProposalAction(proposal._id, "star");
              }}
            />
          )}
          {user?.imagenDePerfil ? (
            <AssetImage
              src={user.imagenDePerfil}
              alt={t("common.avatarAlt")}
              className={styles.userAvatar}
            />
          ) : user?.nombre ? (
            <div className={styles.userAvatar}>{user.nombre.charAt(0)}</div>
          ) : (
            <div className={styles.userAvatar}></div>
          )}
          <div className={styles.userInfoText}>
            <span className={styles.userName}>
              {user?.nombre || t("common.userNotFound")}
            </span>
            <span className={styles.userEmail}>
              ({user?.correoElectronico || ""})
            </span>
            <div className={styles.companyName}>
              {company?.nombreComercial || t("common.defaultCompanyName")}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.proposalCell}>
        <div className={styles.actionButtonsContainer}>
          <IconButton
            color="success"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onProposalAction(proposal._id, "approve");
            }}
            disabled={isAccepted}
            sx={{
              "& .MuiSvgIcon-root": {
                color: isAccepted ? "#B3E6C3" : "#00E864",
              },
              "&.Mui-disabled": {
                color: "#B3E6C3",
              },
            }}
          >
            <CheckCircleIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onProposalAction(proposal._id, "reject");
            }}
            disabled={isRejected}
            sx={{
              "& .MuiSvgIcon-root": {
                color: isRejected ? "#F7B3B3" : "#EF183B",
              },
              "&.Mui-disabled": {
                color: "#F7B3B3",
              },
            }}
          >
            <CancelIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default Proposal;
